/**
 * Account herstellen
 * ══════════════════
 *
 * TWEE ROUTES, OMDAT ER TWEE SOORTEN GEBRUIKERS ZIJN
 *
 * Een volwassene heeft een echt e-mailadres en krijgt een herstelmail. Een
 * kind niet: zijn auth-adres eindigt op `.invalid` en kan geen post ontvangen.
 * Voor hem is er de herstelcode van zes woorden die hij bij registratie kreeg.
 *
 * Dit bestand is de clientkant van beide. De leerlingroute loopt verplicht via
 * de Edge Function `recover-account`, want twee dingen die daar gebeuren mogen
 * niet in de browser: `verify_recovery_code` (anders is het een gratis orakel
 * om codes te beproeven) en het zetten van het nieuwe wachtwoord (dat raakt
 * `auth.users` en vereist de service-role).
 *
 *
 * WAT WE HIER BEWUST NIET DOEN
 *
 * Geen vooraf-controle of de gebruikersnaam bestaat, en geen onderscheid
 * tussen "die naam kennen we niet" en "die code klopt niet". Beide zouden dit
 * scherm veranderen in een manier om te achterhalen welke kinderen een account
 * hebben. De server geeft daarom één en dezelfde melding terug, en die nemen
 * we hier letterlijk over in plaats van hem te verfijnen.
 */

import { supabase } from './client';
import { requestPasswordReset as requestAdultReset } from './auth';
import { error as logError } from '../../utils/logger';

/** Zelfde ondergrens als de Edge Function en het registratieformulier. */
export const MIN_PASSWORD_LENGTH = 8;

/** Namespace van solo-accounts. Zie de waarschuwing in `authService`. */
const LEARNER_DEFAULT_NAMESPACE = 'solo';
export interface RecoveryResult {
  ok: boolean;
  message?: string;
}

/**
 * Herstel een leerlingaccount met gebruikersnaam + herstelcode.
 *
 * Bij succes is het wachtwoord gewijzigd én zijn alle bestaande sessies
 * ingetrokken — als iemand anders het account had overgenomen, vliegt die er
 * op dit moment uit. Het kind moet daarna gewoon opnieuw inloggen.
 */
export async function recoverLearnerAccount(input: {
  username: string;
  code: string;
  newPassword: string;
}): Promise<RecoveryResult> {
  const username = input.username.trim().toLowerCase();
  const code = input.code.trim().toLowerCase();
  if (!username || !code) {
    return {
      ok: false,
      message: 'Vul je gebruikersnaam en je herstelcode in.'
    };
  }
  if (input.newPassword.length < MIN_PASSWORD_LENGTH) {
    return {
      ok: false,
      message: `Je nieuwe wachtwoord moet minimaal ${MIN_PASSWORD_LENGTH} tekens zijn.`
    };
  }
  try {
    const {
      data,
      error
    } = await supabase.functions.invoke('recover-account', {
      body: {
        username,
        usernameNamespace: LEARNER_DEFAULT_NAMESPACE,
        code,
        newPassword: input.newPassword
      }
    });
    if (error) {
      // De herstelcode komt NIET in de log — dat zou hem leesbaar maken voor
      // iedereen die de console openslaat op een gedeeld apparaat.
      logError('[recover-account] mislukt', (error as Error)?.message ?? String(error));
      return {
        ok: false,
        message: await readFunctionError(error)
      };
    }
    if (!data?.ok) {
      // 2xx zonder `ok`: dan antwoordde er iets anders dan onze functie, bijna
      // altijd de gateway omdat de functie niet is uitgerold.
      logError('[recover-account] onverwacht antwoord', JSON.stringify(data));
      return {
        ok: false,
        message: 'Herstellen is niet gelukt. Probeer het later opnieuw.'
      };
    }
    return {
      ok: true
    };
  } catch (e) {
    logError('[recover-account] mislukt', e);
    return {
      ok: false,
      message: 'Herstellen is niet gelukt. Probeer het later opnieuw.'
    };
  }
}

/**
 * Stuur een herstelmail naar een volwassene.
 *
 * De melding is altijd dezelfde, ook bij een onbekend adres. Anders wordt dit
 * formulier een manier om te achterhalen welke e-mailadressen een account
 * hebben — en die adressen horen bij mensen die aan kinddata gekoppeld zijn.
 */
export async function recoverAdultAccount(email: string): Promise<RecoveryResult> {
  const trimmed = email.trim();
  if (!trimmed || !trimmed.includes('@')) {
    return {
      ok: false,
      message: 'Vul een geldig e-mailadres in.'
    };
  }
  const result = await requestAdultReset(trimmed);
  return {
    ok: result.ok,
    message: result.message
  };
}

/**
 * Haalt de Nederlandse melding uit een mislukte Edge Function-aanroep.
 *
 * `functions.invoke` gooit bij 4xx/5xx een fout waarvan de `message` alleen de
 * statuscode noemt; de bruikbare tekst zit in de body.
 */
async function readFunctionError(error: unknown): Promise<string> {
  const fallback = 'Herstellen is niet gelukt. Probeer het later opnieuw.';
  const context = (error as {
    context?: Response;
  })?.context;
  if (!context || typeof context.json !== 'function') return fallback;
  try {
    const body = await context.json();
    return typeof body?.error === 'string' ? body.error : fallback;
  } catch {
    return fallback;
  }
}